import React from "react";
import SourcePill from "../SourcePill/SourcePill";
import { extractUrlValues } from "../../helper/helper";
import { Link } from "react-router-dom";
import styles from "./InternalLinkSourcePill.module.css";
import { EventSourcesMap } from "../../common-types/EventSource";
import { Osdk } from "@osdk/client";
import { Analysis } from "@unruly-osdk/sdk";

interface InternalLinkSourcePillProps {
  href: string;
  eventSources: EventSourcesMap;
  isLoading: boolean;
  missingEventIds: string[];
  userId?: string;
  sourceNumberMap?: Map<string, number>;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog: (eventId: string) => void;
}

const InternalLinkSourcePill: React.FC<InternalLinkSourcePillProps> = ({
  href,
  eventSources,
  isLoading,
  missingEventIds,
  userId,
  sourceNumberMap,
  onOpenAnalysis,
  showShareDialog,
}) => {
  const urlValues = extractUrlValues(href);
  const eventId = urlValues.primaryKey;

  // If it's not an event link, show the original href
  if (urlValues.targetObjectType !== "event") {
    return <span>{href}</span>;
  }

  // If we're loading, show a loading state
  if (isLoading) {
    return <span>...</span>;
  }

  // If we don't have an event ID, show the original href
  if (!eventId) {
    return <Link to={href}>{href}</Link>;
  }

  // Check if this event is missing (doesn't exist in the database)
  const isMissingEvent = missingEventIds.includes(eventId);

  // If the event is missing, show a fallback with just the event ID
  if (isMissingEvent) {
    return (
      <span className={styles.missingEvent} title={eventId}>
        {eventId.slice(0, 4)}...{eventId.slice(-4)}
      </span>
    );
  }

  const eventData = eventSources[eventId];

  if (!eventData || (!eventData.sourceUrl && !eventData.isInternalEvent)) {
    return <Link to={href}>(source)</Link>;
  }

  return (
    <SourcePill
      sourceUrl={eventData.sourceUrl}
      sourceLabel={eventData.sourceLabel}
      sourceName={eventData.sourceName}
      eventId={eventId}
      eventName={eventData.eventName}
      eventSummary={eventData.eventSummary}
      eventDate={eventData.eventDate}
      userId={userId}
      sourceNumber={sourceNumberMap?.get(eventId)}
      onOpenAnalysis={onOpenAnalysis}
      showShareDialog={showShareDialog}
    />
  );
};

export default InternalLinkSourcePill;
